import React, { Component, createRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { TAG_PARAM, tagSlugsFromParam } from '../../util/listingTagSearch';
import { useConfiguration } from '../../context/configurationContext';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';
import { loadMoreListings, fetchNewestListings } from './SearchPage.duck';
import {
  peekPendingRoute,
  clearPendingRoute,
  recordSearch,
  setCurrentSearch,
  clearCurrentSearch,
  getCurrentSearch,
  recordSearchEvent,
  recordListingClick,
  generateUuid,
  flushAbandonedSearch,
  searchedAtIso,
  RESULT_COUNT_STATUS,
} from '../../util/searchTelemetry';

import FilterComponent from './FilterComponent';
import AppliedFilterChips from './AppliedFilterChips/AppliedFilterChips';
import SearchFilterDrawer from './SearchFilterDrawer/SearchFilterDrawer';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

// Listing-field filter groups hidden from the filter drawer. These fields stay
// indexed and filterable (Console config untouched): a deep link or AI-routed
// search carrying e.g. pub_pricingOption=... still filters results and still
// renders a removable chip — only the drawer checkboxes are gone.
const SEARCH_PAGE_HIDDEN_FILTER_KEYS = ['pricingOption'];

// Listing-field filter groups that open collapsed in the drawer. State/Province
// carries 60 options — more rows than every other group combined — so leaving it
// expanded buries Lifestage and Birth Year below ~1600px of scroll. Only the
// initial state changes: the toggle and chevron behave as they always have, and
// an applied selection still shows the group's count badge (plus its removable
// chips above the results), so a collapsed group never hides an active filter.
const SEARCH_PAGE_COLLAPSED_FILTER_KEYS = ['locationState'];

// The URL the filter state is derived from: path and query (Story 8.18 fix review #4).
const urlKey = location => `${location?.pathname || ''}${location?.search || ''}`;

// Filter-lines icon for the Filters toggle pill (gold strokes per design ref)
const FilterLinesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="14" y2="12" />
    <line x1="4" y1="18" x2="9" y2="18" />
  </svg>
);

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFilterDrawerOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      // The URL `currentQueryParams` was derived from (getDerivedStateFromProps).
      currentQueryParamsUrl: urlKey(props.location),
    };

    // Focus returns here when the filter drawer closes
    this.filtersToggleRef = createRef();

    this.onOpenFilterDrawer = this.onOpenFilterDrawer.bind(this);
    this.onCloseFilterDrawer = this.onCloseFilterDrawer.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Story 8.2 (AC9) — search telemetry
    this.maybeRecordSearch = this.maybeRecordSearch.bind(this);
    this.fireChipModifiedEvent = this.fireChipModifiedEvent.bind(this);
    this.handleListingClick = this.handleListingClick.bind(this);

    // Bumped by Clear all; a filter handler made before the bump is dropped
    // (Story 8.18 fix review #2).
    this.clearAllCount = 0;
  }

  /**
   * Story 8.18 ID-14 (ledger #3106) — the filter state is the URL's, re-derived
   * whenever the URL changes. The page stays mounted across every /s
   * navigation, and the filter handler builds the next URL from
   * `{ ...urlQueryParams, ...currentQueryParams }`, so a state seeded once at
   * mount overrode the URL with whatever it held: a chip removed by writing the
   * URL directly (the keyword chip, the "Described as" chip), a new typed
   * search, or the back button came back on the buyer's next chip change.
   *
   * Keyed on the URL itself — path and query, since the path's params also
   * shape the valid filters (fix review #4) — so every writer is covered
   * without each having to remember to reset it. After the handler's own push
   * this re-reads what it pushed.
   *
   * One draft does outlive a URL change: the Price and Birth Year boxes submit
   * through a 400 ms debounce (IntegerRangeFilter), from the render where the
   * buyer typed. A selection removed inside that window is absent from the
   * re-derived state, so the handler must not fill it back from the URL it
   * captured: it reads the URL when the update runs (`getUrlQueryParams` below;
   * Story 8.18 fix review #1 and #2).
   */
  static getDerivedStateFromProps(props, state) {
    const url = urlKey(props.location);
    if (url === state.currentQueryParamsUrl) {
      return null;
    }
    return {
      currentQueryParams: validUrlQueryParamsFromProps(props),
      currentQueryParamsUrl: url,
    };
  }

  /**
   * Story 8.2 (AC9) — a result click within a recorded search is the
   * click-through signal. Fire-and-forget; recordListingClick no-ops when there
   * is no stored search_id (untracked view) and never blocks navigation.
   */
  handleListingClick(listingUuid) {
    recordListingClick(listingUuid);
  }

  // Story 8.2 (AC9) — after results render, perform the single /search/record.
  componentDidMount() {
    this.maybeRecordSearch();
  }

  componentDidUpdate() {
    this.maybeRecordSearch();
  }

  /**
   * Issue the single deferred /search/record once, after a fresh Topbar-submitted
   * search has rendered its results. Reads the pending route stashed at submit
   * time; a reload / shared link / pagination has no pending route, so this fires
   * NEITHER /search/route NOR /search/record (re-hydration from URL — AC9).
   *
   * Story 8.11 — this method used to log `pagination.totalItems` whenever a
   * pending route matched the URL, with NO gate asserting that the pagination in
   * the store belonged to the search the row was about. It did not:
   * `componentDidUpdate` runs child-first, so on the commit caused by the URL
   * push this method ran BEFORE the parent RouteComponentRenderer dispatched
   * `loadData`. `searchInProgress` was still false and `pagination` still held
   * the PREVIOUS search's meta, so the row went out against stale data — and
   * because `clearPendingRoute()` is consume-once and fires before the network
   * call, the later render carrying the correct pagination found nothing pending
   * and returned. The fabricated row REPLACED the true one rather than joining
   * it; ~49% of production rows carried a count from a different search.
   *
   * The fix is the `completedSearchKey` gate below.
   */
  maybeRecordSearch() {
    if (typeof window === 'undefined') {
      return;
    }
    const {
      location,
      pagination,
      searchInProgress,
      completedSearchKey,
      searchOutcome,
    } = this.props;
    if (searchInProgress) {
      return;
    }

    // ── AC1: the completed-search gate ───────────────────────────────────────
    // EXACT STRING EQUALITY between the router query string this commit is
    // rendering and the one stamped on the store's outcome by the fulfilled /
    // rejected reducer. Both originate from the same router `location` object in
    // the same commit (Routes.js passes `location.search` into loadData; this
    // component reads `this.props.location.search`), so a byte comparison is the
    // strongest available proof that the outcome in the store was produced BY
    // this search — not merely that some search has finished at some point.
    //
    // ⚠️ Do NOT weaken this to compare `state.searchParams` against the URL:
    // loadData strips `aiKeywords` before dispatching, so `searchParams`
    // structurally cannot identify an ai_iso search. Nor reconstruct/re-stringify
    // the query — parameter reordering would silently defeat the comparison.
    const searchKey = location?.search || '';
    if (completedSearchKey !== searchKey) {
      return;
    }
    // Neither a completed nor a failed search (e.g. hydrated state with no
    // outcome yet) → nothing trustworthy to say about this URL.
    if (searchOutcome !== 'measured' && searchOutcome !== 'error') {
      return;
    }

    const params = parse(location?.search || '');
    const rawQuery =
      params.aiKeywords != null
        ? String(params.aiKeywords)
        : params.keywords != null
        ? String(params.keywords)
        : null;
    if (!rawQuery) {
      return;
    }

    const pending = peekPendingRoute();
    // No pending route → re-hydration from URL (reload / shared link / SSR
    // entry): fire nothing. This is the gate that keeps AC9 true, and after AC1
    // it is no longer the ONLY thing standing between hydration and a record —
    // but it is still the thing doing it, so it must not be relaxed.
    if (!pending) {
      return;
    }
    // A pending route for a DIFFERENT query while this query's results are on
    // screen → that search is provably abandoned too. Secondary AC2b flush, at
    // zero cost: flushAbandonedSearch() re-peeks and clears the same key in the
    // same synchronous tick, so it consumes exactly this route and no other.
    if (pending.raw_query !== rawQuery) {
      flushAbandonedSearch();
      return;
    }

    // ── AC2: the error leg ───────────────────────────────────────────────────
    // A rejected search leaves `state.pagination` untouched — it still holds the
    // previous search's meta. Read it here and the failure would be logged as a
    // healthy row carrying someone else's count (§1.3, the latent path this
    // closes structurally). So the count is NULL and the status says why.
    //
    // `latency_ms` IS sent on this leg, unlike the abandon flush: the request
    // genuinely ran and genuinely took this long to fail, so the number is
    // measured rather than invented. Latency analyses filter to
    // `result_count_status = 'measured'`, so it never pollutes that distribution.
    const isError = searchOutcome === 'error';

    let resultCount = null;
    if (!isError) {
      const totalItems = pagination?.totalItems;
      if (typeof totalItems !== 'number') {
        // 'measured' outcome with no usable count: incoherent, and there is no
        // honest number to send. Return WITHOUT consuming the pending route, so
        // this search is either recorded correctly on a later pass or flushed as
        // `unmeasured` by the next submit. Never fabricate to fill the column.
        return;
      }
      resultCount = totalItems;
    }

    // Consume-once: clear before the async call so a re-render can't double-record.
    clearPendingRoute();
    const latencyMs = Math.max(0, Math.round(Date.now() - (pending.ts || Date.now())));
    const body = {
      raw_query: pending.raw_query,
      visitor_session_id: pending.visitor_session_id,
      routed_to: pending.routed_to,
      routing_reason: pending.routing_reason,
      route_context: pending.route_context,
      result_count: resultCount,
      result_count_status: isError ? RESULT_COUNT_STATUS.ERROR : RESULT_COUNT_STATUS.MEASURED,
      latency_ms: latencyMs,
      searched_at: searchedAtIso(pending),
    };
    if (pending.user_uuid) {
      body.user_uuid = pending.user_uuid;
    }

    recordSearch(body)
      .then(resp => {
        if (resp && resp.search_id != null) {
          // started_at drives first-click latency for subsequent click events.
          setCurrentSearch({
            search_id: resp.search_id,
            raw_query: pending.raw_query,
            started_at: pending.ts || Date.now(),
          });
        } else {
          // NIT-3: record returned no search_id → drop any prior current-search
          // so later clicks/chips aren't mis-attributed to it.
          clearCurrentSearch();
        }
      })
      .catch(() => {
        // Record failed → same: don't let a stale search_id mis-attribute events.
        clearCurrentSearch();
      });
  }

  /** Fire a chip_modified event for the current search when a filter is changed. */
  fireChipModifiedEvent() {
    if (typeof window === 'undefined') {
      return;
    }
    const current = getCurrentSearch();
    if (!current || current.search_id == null) {
      return;
    }
    recordSearchEvent({
      event_id: generateUuid(),
      event_type: 'chip_modified',
      search_id: current.search_id,
      occurred_at: new Date().toISOString(),
      payload: {},
    }).catch(() => {
      /* fire-and-forget */
    });
  }

  onOpenFilterDrawer() {
    this.setState({ isFilterDrawerOpen: true });
  }

  onCloseFilterDrawer() {
    this.setState({ isFilterDrawerOpen: false });
    // Mirror the hamburger-menu behavior: focus returns to the toggle.
    this.filtersToggleRef.current?.focus();
  }

  // Reset all filter query parameters AND the keyword search.
  // keywords/aiKeywords are pass-through params (not filter params), so
  // onResetAll's filter-param omit would keep them — strip them here.
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { keywords, aiKeywords, ...urlQueryParams } = validUrlQueryParamsFromProps(this.props);
    // A Price or Birth Year value typed just before Clear all is still waiting
    // on its 400 ms debounce; it must not land after the clear.
    this.clearAllCount += 1;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams,
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    const baseHandler = createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        // Read when the update runs, not now: a debounced submit can land after
        // the URL has changed (Story 8.18 fix review #1).
        getUrlQueryParams: () => validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
    const clearAllCountAtCreation = this.clearAllCount;
    // Story 8.2 (AC9) — a filter refinement is a chip_modified telemetry event on
    // the current search (not a new search). Best-effort; never blocks the filter.
    return (...args) => {
      // Made before a Clear all: only a debounced submit can still be holding
      // this handler, and the buyer has cleared what it carries.
      if (clearAllCountAtCreation !== this.clearAllCount) {
        return;
      }
      this.fireChipModifiedEvent();
      return baseHandler(...args);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      onLoadMore,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
      newestListings = [],
      newestFetched,
      newestInProgress,
      onFetchNewestListings,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    // Shared chips component: header row + empty state render the same one.
    const appliedFilterChips = chipsClassName => (
      <AppliedFilterChips
        className={chipsClassName}
        location={location}
        validQueryParams={validQueryParams}
        availableFilters={availableFilters}
        getHandleChangedValueFn={this.getHandleChangedValueFn}
        onClearAll={this.resetAll}
        onChipModified={this.fireChipModifiedEvent}
        marketplaceCurrency={marketplaceCurrency}
      />
    );

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        appliedFilterChips={appliedFilterChips(css.emptyStateChips)}
        newestListings={newestListings}
        newestFetched={newestFetched}
        newestInProgress={newestInProgress}
        onFetchNewestListings={onFetchNewestListings}
      />
    );

    // Keyword search active? Same precedence as maybeRecordSearch.
    const rawSearchParams = parse(location.search || '');
    const hasActiveKeyword = (rawSearchParams.aiKeywords ?? rawSearchParams.keywords) != null;
    // Story 8.18 (review L3) — the listing-tag filter narrows the results but is
    // not a hosted filter, so validQueryParams (and the drawer's badge count,
    // which counts what the drawer can change) never see it. Read it from the
    // URL, as the chip does, so a tag-only search still says "matching your search".
    const hasActiveTagFilter = tagSlugsFromParam(rawSearchParams[TAG_PARAM]).length > 0;
    const hasActiveRefinement =
      hasActiveKeyword || hasActiveTagFilter || selectedFiltersCountForMobile > 0;

    // Site header — the homepage header pinned in its solid (docked) state.
    // While the filter drawer is open the behind-modal class drops the header
    // below the drawer's stacking context (it composes .topbar, so it carries
    // the base header styles too).
    const topbarClasses = this.state.isFilterDrawerOpen ? css.topbarBehindModal : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentSearchParams={validQueryParams}
          variant="homepage"
          solid
        />
        <div className={css.layoutWrapperContainer}>
          <SearchFilterDrawer
            isOpen={this.state.isFilterDrawerOpen}
            onClose={this.onCloseFilterDrawer}
            onClearAll={e => this.handleResetAll(e)}
            resultsCount={totalItems}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            {availableFilters
              .filter(filterConfig => !SEARCH_PAGE_HIDDEN_FILTER_KEYS.includes(filterConfig.key))
              .map(filterConfig => {
                const key = `SearchFilterDrawer.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                const filterId = `SearchFilterDrawer.${filterConfig.key.toLowerCase()}`;
                // Passed only for the collapsed keys, so every other group's
                // prop surface is untouched and keeps FilterPlain's default.
                const initialIsOpenMaybe = SEARCH_PAGE_COLLAPSED_FILTER_KEYS.includes(
                  filterConfig.key
                )
                  ? { initialIsOpen: false }
                  : {};
                return (
                  <FilterComponent
                    key={key}
                    id={filterId}
                    config={filterConfig}
                    {...initialIsOpenMaybe}
                    containerId="SearchPage_DrawerFilters"
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                  />
                );
              })}
          </SearchFilterDrawer>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainerGrid}>
              {/* Mobile (<768px): rows 1+2 stick beneath the site header while
                  the list scrolls (design ref M4). Desktop: plain header row. */}
              <div className={css.controlBar}>
                <div className={css.headerRow}>
                  <button
                    type="button"
                    ref={this.filtersToggleRef}
                    className={css.filtersToggle}
                    onClick={this.onOpenFilterDrawer}
                    aria-expanded={this.state.isFilterDrawerOpen}
                    aria-haspopup="dialog"
                  >
                    <FilterLinesIcon />
                    <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
                    {selectedFiltersCountForMobile > 0 ? (
                      <span className={css.filtersToggleCount}>
                        {selectedFiltersCountForMobile}
                      </span>
                    ) : null}
                  </button>
                  {appliedFilterChips(css.headerChips)}
                  {sortConfig.active ? (
                    <div className={css.sortWrap}>
                      <span className={css.sortByLabel}>
                        <FormattedMessage id="MainPanelHeader.sortBy" />
                      </span>
                      {sortBy('desktop')}
                    </div>
                  ) : null}
                </div>
              </div>
              <h1 className={css.countLine}>
                {searchInProgress ? (
                  <FormattedMessage id="MainPanelHeader.loadingResults" />
                ) : (
                  <>
                    <FormattedMessage
                      id="MainPanelHeader.foundResults"
                      values={{ count: totalItems }}
                    />
                    {hasActiveRefinement ? (
                      <span className={css.countLineSuffix}>
                        {' '}
                        <FormattedMessage id="SearchPage.countLineSuffix" />
                      </span>
                    ) : null}
                  </>
                )}
              </h1>
              {noResultsInfo}
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  onLoadMore={onLoadMore}
                  searchInProgress={searchInProgress}
                  intl={intl}
                  onListingClick={this.handleListingClick}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const selectNewestListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const {
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    accumulatedResultIds,
    viewMorePage,
    newestFetched,
    newestInProgress,
    // Story 8.11 (AC1/AC2) — which search the store's outcome belongs to.
    completedSearchKey,
    searchOutcome,
  } = useSelector(state => state.SearchPage);

  // Read accumulatedResultIds from inside the selector (same pattern as original currentPageResultIds)
  // so the snapshot is never stale when useSyncExternalStore checks for changes.
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.accumulatedResultIds)
  );
  // Newest active listings for the empty-state "Newest on The Premier Horse" row
  const newestListings = useSelector(state =>
    selectNewestListingsById(state, state.SearchPage.newestListingIds || [])
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onFetchNewestListings = useCallback(() => dispatch(fetchNewestListings({ config })), [
    dispatch,
    config,
  ]);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const onLoadMore = useCallback(() => {
    if (!searchParams) return;
    const nextPage = viewMorePage + 1;

    // Update URL without triggering React Router navigation / loadData reset.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);
      window.history.replaceState(null, '', url.toString());
    } catch (_) {}

    dispatch(
      loadMoreListings({ searchParams: { ...searchParams, page: nextPage, perPage: 12 }, config })
    );
  }, [dispatch, searchParams, viewMorePage, config]);

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      completedSearchKey={completedSearchKey}
      searchOutcome={searchOutcome}
      onManageDisableScrolling={onManageDisableScrolling}
      onLoadMore={onLoadMore}
      newestListings={newestListings}
      newestFetched={newestFetched}
      newestInProgress={newestInProgress}
      onFetchNewestListings={onFetchNewestListings}
    />
  );
};

export default SearchPage;
